import { useMemo, useState } from "react";
import { ForkKnife, Sun, Moon } from "@phosphor-icons/react";

/**
 * BrandingPreview — live mini-widget rendered right next to the
 * Website Title input so operators see exactly what the browser tab,
 * favicon, and login card will look like BEFORE they hit save.
 *
 * Includes a light/dark theme toggle so operators can eyeball how the
 * branding lands in both surfaces without leaving the settings page.
 * When the operator uploads a logo with transparency, we render it on
 * the neutral surface color (muted) instead of the primary orange so
 * the alpha channel blends into the surrounding chrome.
 */
const BrandingPreview = ({ websiteTitle, companyName, logoUrl }) => {
  const [dark, setDark] = useState(false);

  const effectiveTitle = useMemo(() => {
    return (
      (websiteTitle && websiteTitle.trim()) ||
      (companyName && companyName.trim()) ||
      "Crest POS"
    );
  }, [websiteTitle, companyName]);

  const truncatedTabTitle = effectiveTitle.length > 26
    ? `${effectiveTitle.slice(0, 25)}…`
    : effectiveTitle;

  // Manual palette so the preview reflects the picked theme regardless
  // of the app's own current mode. Keeps the settings page from having
  // to flip its actual class.
  const palette = dark
    ? {
        outer: "bg-neutral-800 border-neutral-700 text-neutral-100",
        muted: "text-neutral-400",
        tab: "bg-neutral-900 border-neutral-700 text-neutral-100",
        tabBar: "border-neutral-700",
        card: "bg-neutral-900 border-neutral-700 text-neutral-100",
        logoSurface: "bg-neutral-700",
        primary: "bg-orange-500",
        subMuted: "text-neutral-400",
      }
    : {
        outer: "bg-secondary/30 border-border text-foreground",
        muted: "text-muted-foreground",
        tab: "bg-background border text-foreground",
        tabBar: "border-b",
        card: "bg-background border text-foreground",
        logoSurface: "bg-muted",
        primary: "bg-primary",
        subMuted: "text-muted-foreground",
      };

  return (
    <div
      className={`rounded-lg border p-3 space-y-3 transition-colors ${palette.outer}`}
      data-testid="branding-preview"
      data-theme={dark ? "dark" : "light"}
    >
      <div className="flex items-center justify-between">
        <div className={`text-[10px] uppercase tracking-widest ${palette.muted}`}>
          Live preview
        </div>
        <div
          className={`inline-flex rounded-full border overflow-hidden ${dark ? "border-neutral-600" : "border-border"}`}
          data-testid="branding-preview-theme-toggle"
        >
          <button
            type="button"
            onClick={() => setDark(false)}
            className={`px-2 py-1 text-[10px] flex items-center gap-1 transition-colors ${
              !dark
                ? "bg-primary text-primary-foreground"
                : `${palette.muted} hover:opacity-80`
            }`}
            data-testid="branding-preview-toggle-light"
            aria-pressed={!dark}
          >
            <Sun size={10} weight="fill" /> Light
          </button>
          <button
            type="button"
            onClick={() => setDark(true)}
            className={`px-2 py-1 text-[10px] flex items-center gap-1 transition-colors ${
              dark
                ? "bg-primary text-primary-foreground"
                : `${palette.muted} hover:opacity-80`
            }`}
            data-testid="branding-preview-toggle-dark"
            aria-pressed={dark}
          >
            <Moon size={10} weight="fill" /> Dark
          </button>
        </div>
      </div>

      {/* Browser tab mock. */}
      <div className="space-y-1" data-testid="branding-preview-tab">
        <div className={`text-[10px] ${palette.muted}`}>Browser tab</div>
        <div className="flex items-end gap-1">
          <div
            className={`flex items-center gap-1.5 rounded-t-md px-2.5 py-1.5 max-w-[260px] shadow-sm border border-b-0 ${palette.tab}`}
          >
            {logoUrl ? (
              <img
                src={logoUrl}
                alt=""
                className={`w-3.5 h-3.5 object-contain rounded-sm shrink-0 ${palette.logoSurface}`}
                data-testid="branding-preview-favicon"
              />
            ) : (
              <div
                className={`w-3.5 h-3.5 rounded-sm ${palette.primary} flex items-center justify-center shrink-0`}
                data-testid="branding-preview-favicon-fallback"
              >
                <ForkKnife size={9} weight="fill" className="text-primary-foreground" />
              </div>
            )}
            <span
              className="text-[11px] font-medium truncate"
              data-testid="branding-preview-tab-title"
            >
              {truncatedTabTitle}
            </span>
            <span className={`pl-1 shrink-0 opacity-50 ${palette.muted}`}>×</span>
          </div>
          <div className={`flex-1 h-6 ${palette.tabBar}`} />
        </div>
      </div>

      {/* Login card mock. */}
      <div className="space-y-1" data-testid="branding-preview-login">
        <div className={`text-[10px] ${palette.muted}`}>Login screen</div>
        <div className={`rounded-md p-3 text-center space-y-1.5 border ${palette.card}`}>
          {logoUrl ? (
            <div
              className={`w-9 h-9 rounded ${palette.logoSurface} mx-auto flex items-center justify-center overflow-hidden`}
            >
              <img
                src={logoUrl}
                alt=""
                className="w-full h-full object-contain"
                data-testid="branding-preview-login-logo"
              />
            </div>
          ) : (
            <div className={`w-9 h-9 rounded ${palette.primary} flex items-center justify-center mx-auto`}>
              <ForkKnife size={20} weight="fill" className="text-primary-foreground" />
            </div>
          )}
          <div
            className="font-heading text-base font-semibold tracking-tight leading-tight"
            data-testid="branding-preview-login-name"
          >
            {effectiveTitle}
          </div>
          <div className={`text-[10px] ${palette.subMuted}`}>
            Enter your 4-digit PIN to sign in
          </div>
        </div>
      </div>

      {(!websiteTitle || !websiteTitle.trim()) && (
        <p
          className={`text-[10px] italic ${palette.muted}`}
          data-testid="branding-preview-fallback-note"
        >
          Using Company Name as the fallback title.
        </p>
      )}
    </div>
  );
};

export default BrandingPreview;
